/// <amd-module name="Repos/Views/Settings/Repositories/Components/RepositoriesMasterPanel" />
import React = require("react");
import { CheckIssueType } from "Checks/Client/Contracts/PipelinesChecks";
import { ISettingsViewService } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories/Resources";
import { IRepositoriesViewService } from "Repos/Views/Settings/Repositories/Services/RepositoriesViewService";
import { IObservableValue, IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent, css } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { IListItemDetails, IListSelection, List, ListItem, ListSelection } from "VSSUI/List";
import { IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import { ISelectionRange } from "VSSUI/Utilities/Selection";

interface RepositoriesMasterPanelProps {
    selected: IObservableValue<IRepository>;
}

export class RepositoriesMasterPanel extends VssComponent<RepositoriesMasterPanelProps> {
    private selected: IReadonlyObservableValue<IRepository | undefined>;
    private data: IReadonlyObservableArray<IRepository>;
    private filter: IFilter;
    private selection: IListSelection;
    private settingsSvc: ISettingsViewService;
    private svc: IRepositoriesViewService;

    public constructor(props: RepositoriesMasterPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.selection = new ListSelection();
        this.selection.selectOnFocus = false;
        this.svc = context.pageContext.getService<IRepositoriesViewService>("RepositoriesViewService");
        this.settingsSvc = context.pageContext.getService<ISettingsViewService>("SettingsViewService");

        this.data = this.svc.getFilteredRepositories() as IReadonlyObservableArray<IRepository>; // other types will only be returned when data is loading, and this component is not reachable;
        this.filter = this.svc.getFilter();
        this.selected = this.svc.getSelected();
        this.setSelectedIndex();
    }

    public componentDidMount() {
        this.selected.subscribe(this.setSelectedIndex);
        this.selection.subscribe(this.onSelectionChange);
        this.filter.subscribe(this.onFilterChange);
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this.selected.unsubscribe(this.setSelectedIndex);
        this.selection.unsubscribe(this.onSelectionChange);
        this.filter.unsubscribe(this.onFilterChange);
    }

    public render = (): JSX.Element => {
        return (
            <List itemProvider={this.data} renderRow={this.renderRow} selection={this.selection} width="100%" ariaLabel={Resources.Repositories} />
        );
    };

    private onClick = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement> | undefined,
        item: IRepository
    ) => {
        if (!event || (!event.shiftKey && !event.ctrlKey)) {
            this.svc.setSelected(item);
            this.setSelectedIndex();

            if (event) {
                event.preventDefault();
            }
        }
    };

    private onFilterChange = (state: IFilterState) => {
        const filterItem = state["name"];
        if (!filterItem || filterItem.value === "") {
            // filter was reset, and we may need to set selected again

            this.setSelectedIndex();
        }
    };

    private onSelectionChange = (selectedRanges: ISelectionRange[]) => {
        if (selectedRanges[0]) {
            const index = selectedRanges[0].beginIndex;
            const endIndex = selectedRanges[0].endIndex;

            if (index != endIndex) {
                // this is not a user selection so need to see if currently selected item is in filtered list
                this.setSelectedIndex();
            }
        }
    };

    private renderRow = (index: number, item: IRepository, details: IListItemDetails<IRepository>): JSX.Element => {
        return (
            <ListItem
                linkProps={{ href: this.settingsSvc.getUrl(item.id, undefined, undefined), onClick: event => this.onClick(event, item) }}
                key={item.id}
                index={index}
                details={details}
                className="settings-repositories-master-panel-row no-underline-link"
            >
                <div className="flex-row h-scroll-hidden padding-horizontal-16 padding-vertical-4">
                    <Icon {...item.iconProps} className={css("padding-horizontal-8 padding-vertical-4 font-size-ml", item.iconProps.className)} />
                    <span className="text-ellipsis flex-self-center">{item.name}</span>
                    {item.issue && (
                        <Icon
                            className={item.issue.type === CheckIssueType.Error ? "resource-error-icon" : "resource-warning-icon"}
                            iconName={item.issue.type === CheckIssueType.Error ? "ErrorBadge" : "Warning"}
                            tooltipProps={{
                                text: item.issue.message
                            }}
                        />
                    )}
                </div>
            </ListItem>
        );
    };

    private setSelectedIndex = () => {
        const matchingIndex = this.data.value.findIndex(x => x === this.selected.value);
        if (matchingIndex >= 0) {
            this.selection.select(matchingIndex);
        }
    };
}
